import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import {
    getIsCartInFlightRequests,
    useCartOptimisticUpdateCounterActions,
} from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { cartContainerItemRemoveMutation } from '@/features/pos/components/Cart/mutations'
import {
    getCartFromCache,
    writeCartContainerItemsToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import {
    type CartContainerItemWithIndex,
    findCartContainerItemWithIndex,
    insertCartContainerItem,
    removeCartContainerItem,
} from '@/features/pos/components/Cart/utils/cart-container-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback, useRef } from 'react'
import invariant from 'tiny-invariant'

type RemovedCartContainerItems = Record<string, CartContainerItemWithIndex>
type ClientOptions = { variables?: { cartKey?: string; itemUuid?: string } }

export const useRemoveCartContainerItem = (): ((id: string) => Promise<void>) => {
    const client = useApolloClient()

    const removedItemsRef = useRef<RemovedCartContainerItems>({})

    const { increment, lock, reset } = useCartOptimisticUpdateCounterActions()

    const { cartKey } = useCartKeyContext()

    const withRemovedItem = (
        clientOptions: ClientOptions | undefined,
        callback: (itemWithIndex: CartContainerItemWithIndex, requestCartKey: string) => void
    ) => {
        const itemId = clientOptions?.variables?.itemUuid
        invariant(itemId, "'itemUuid' variable must be set")

        const requestCartKey = clientOptions?.variables?.cartKey
        invariant(requestCartKey, "'cartKey' variable must be set")

        const itemWithIndex = removedItemsRef.current[itemId]
        invariant(itemWithIndex, "Cart container item must be set in 'removedItemsRef'")

        callback(itemWithIndex, requestCartKey)

        reset(itemId)
        delete removedItemsRef.current[itemId]
    }

    const handleError = useHandleErrorWithToast(logger)

    const restoreRemovedItem = (
        itemWithIndex: CartContainerItemWithIndex,
        requestCartKey: string
    ) => {
        const currentCart = getCartFromCache(client.cache, requestCartKey)

        writeCartContainerItemsToCache(
            client.cache,
            requestCartKey,
            insertCartContainerItem(
                currentCart?.containerItems,
                itemWithIndex.item,
                itemWithIndex.index
            )
        )
    }

    const [cartContainerItemRemove] = useMutation(cartContainerItemRemoveMutation, {
        fetchPolicy: 'no-cache',
        onCompleted: (data, clientOptions) => {
            withRemovedItem(clientOptions, (itemWithIndex, requestCartKey) => {
                if (!data.cartContainerItemRemove) {
                    void handleError(new Error('Cart container removal mutation returned no cart'))
                    restoreRemovedItem(itemWithIndex, requestCartKey)
                    return
                }

                if (
                    data.cartContainerItemRemove.containerItems.some(
                        ({ uuid }) => uuid === itemWithIndex.item.uuid
                    )
                ) {
                    void handleError(
                        new Error('Cart container removal mutation returned the removed item')
                    )
                    restoreRemovedItem(itemWithIndex, requestCartKey)
                    return
                }

                if (getIsCartInFlightRequests()) return

                writeCartContainerItemsToCache(
                    client.cache,
                    requestCartKey,
                    data.cartContainerItemRemove.containerItems
                )
            })
        },
        onError: (error, clientOptions) => {
            handleError(error)

            withRemovedItem(clientOptions, restoreRemovedItem)
        }, // needed to prevent app crash when error happened
    })

    return useCallback(
        async (id: string) => {
            invariant(cartKey, "'cartKey' must be set")

            const currentCart = getCartFromCache(client.cache, cartKey)
            const itemWithIndex = findCartContainerItemWithIndex(currentCart?.containerItems, id)

            invariant(itemWithIndex, 'Cart container item must exist')

            increment(id)
            lock(id)

            removedItemsRef.current[id] = itemWithIndex

            writeCartContainerItemsToCache(
                client.cache,
                cartKey,
                removeCartContainerItem(currentCart?.containerItems, id)
            )

            await cartContainerItemRemove({ variables: { cartKey, itemUuid: id } })
        },
        [cartKey, cartContainerItemRemove, client, increment, lock]
    )
}
